<!--
  Fornitore list accounts — redesigned 2026-06-09 (v2.5.0).

  Previous markup was kendo-grid + 5 kendo-dialog modals + ::ng-deep
  overrides. Now plain HTML coherent with the Diakonix family
  (gestione-account, accesso-funzionari). The TS layer was already
  modern (Swal2 + local modal flags + service); only this template
  and the SCSS change.

  Manufacturer role colour is `$warn` (amber) — see SCSS.

  Five modals managed by separate `*Open` flags in TS:
    1. insertOpen    — search a supplier by code
    2. newOpen       — fill the "create supplier account" form
    3. detailOpen    — read-only view of the selected supplier
    4. editOpen      — edit form (nome / cognome / note)
    5. passwordOpen  — change password
-->

<div class="suppliers-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="suppliers-header">
    <div class="suppliers-header__titles">
      <h1 class="suppliers-header__title">🏭 Fornitori</h1>
      <p class="suppliers-header__subtitle">
        @if (sortedSuppliers.length > 0) {
          <strong>{{ sortedSuppliers.length }}</strong> di {{ suppliers.length }} fornitori
        } @else if (loading) {
          Caricamento…
        } @else {
          Nessun fornitore registrato
        }
      </p>
    </div>

    <div class="suppliers-header__controls">
      <div class="search-wrap">
        <input
          type="search"
          class="search-input"
          placeholder="Cerca codice, nome, ragione sociale, email…"
          [(ngModel)]="searchText"
          aria-label="Cerca fornitore"
        />
        @if (searchText) {
          <button
            type="button"
            class="search-clear"
            (click)="clearSearch()"
            aria-label="Cancella ricerca"
            title="Cancella ricerca"
          >×</button>
        }
      </div>

      <button
        type="button"
        class="add-btn"
        (click)="openInsert()"
        title="Aggiungi fornitore"
      >
        <span aria-hidden="true">+</span>
        <span>Aggiungi</span>
      </button>
    </div>
  </header>

  <!-- ─── Table card ────────────────────────────────────────────── -->
  <section class="suppliers-table-card" aria-label="Elenco fornitori">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (sortedSuppliers.length === 0) {
      <div class="suppliers-empty">
        <div class="suppliers-empty__icon" aria-hidden="true">
          @if (sortedSuppliers.length === 0) { 📭 } @else { 🔎 }
        </div>
        <p class="suppliers-empty__msg">
          @if (sortedSuppliers.length === 0) {
            Nessun fornitore registrato. Clicca "Aggiungi" per crearne uno.
          } @else if (searchText) {
            Nessun fornitore corrisponde a "<strong>{{ searchText }}</strong>".
          } @else {
            Nessun risultato.
          }
        </p>
      </div>
    } @else {
      <div class="prep-table-scroll">
        <table class="suppliers-table" role="table">
        <thead>
          <tr>
            <th class="col-customer is-sortable" (click)="sortBy('codice_fornitore')">
              Codice
              @if (sortColumn === 'codice_fornitore') {
                <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
              }
            </th>
            <th class="col-customer is-sortable" (click)="sortBy('fornitore')">
              Ragione Sociale
              @if (sortColumn === 'fornitore') {
                <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
              }
            </th>
            <th class="col-customer is-sortable" (click)="sortBy('ac_cognome')">
              Account
              @if (sortColumn === 'ac_cognome') {
                <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
              }
            </th>            
            <th class="col-customer is-sortable" (click)="sortBy('fornitore_mail')">
              Email
              @if (sortColumn === 'fornitore_mail') {
                <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
              }
            </th>
            <th class="col-status">Stato</th>
            <th class="col-actions" aria-label="Azioni"></th>
          </tr>
        </thead>
        <tbody>
          @for (s of sortedSuppliers; track s.account_id ?? s.fornitore_id) {
            <tr
              class="suppliers-row"
              [class.suppliers-row--inactive]="!isActive(s)"
              (click)="openDetail(s)"
              title="Apri dettagli fornitore"
            >
              <td class="col-code">
                <span class="cell-code">{{ s.codice_fornitore || '—' }}</span>
              </td>
              <td class="col-account">
                <span class="cell-account">
                  @if (s.ac_nome || s.ac_cognome) {
                    {{ s.ac_nome }} {{ s.ac_cognome }}
                  } @else {
                    <span class="cell-muted">—</span>
                  }
                </span>
              </td>
              <td class="col-name">
                <span class="cell-name">{{ s.fornitore || '—' }}</span>
              </td>
              <td class="col-email">
                @if (s.fornitore_mail) {
                  <span class="cell-email">{{ s.fornitore_mail }}</span>
                } @else {
                  <span class="cell-muted">—</span>
                }
              </td>
              <td class="col-status">
                <span class="status-pill" [attr.data-state]="isActive(s) ? 'active' : 'inactive'">
                  @if (isActive(s)) {
                    ● Attivo
                  } @else {
                    ● Disattivato
                  }
                </span>
              </td>
              <td class="col-actions" (click)="$event.stopPropagation()">
                <button
                  type="button"
                  class="icon-btn icon-btn--details"
                  title="Dettagli fornitore"
                  (click)="openDetail(s)"
                >ℹ️</button>
              </td>
            </tr>
          }
        </tbody>
      </table>
      </div>
    }
  </section>

</div>

<!-- ────────────────────────────────────────────────────────────────────
     MODALS — rendered OUTSIDE `.suppliers-page` deliberately.
     `.suppliers-page` has overflow-y:auto and is inside Ionic's
     `.ion-page` wrapper (which uses `contain: layout size style`).
     Both of these can create a containing block for descendant
     `position: fixed`, shifting the modal off the viewport centre.
     Keeping the modals at component root + using 100vw/100dvh
     dimensions explicitly makes the backdrop always span the full
     viewport regardless of CSS containment further up the tree.
     ──────────────────────────────────────────────────────────────── -->

  <!-- ─── Modal 1: Insert (search by code) ───────────────────────── -->
  @if (insertOpen) {
    <div class="modal-backdrop" (click)="insertOpen = false">
      <aside class="modal modal--sm" (click)="$event.stopPropagation()">
        <header class="modal__header">
          <h2 class="modal__title">🔍 Inserisci fornitore</h2>
          <button type="button" class="modal-close" (click)="insertOpen = false" aria-label="Chiudi">×</button>
        </header>
        <div class="modal__body">
          <p class="modal__hint">
            Cerca il fornitore per codice. Una volta trovato potrai creare il suo account.
          </p>
          <div class="modal-form">
            <div class="form-field form-field--full">
              <label class="form-field__label" for="findCode">Codice fornitore</label>
              <input
                id="findCode"
                type="text"
                class="form-input"
                placeholder="Es. 12345"
                [(ngModel)]="findCode"
                (keyup.enter)="findSupplier()"
                autocomplete="off"
                autofocus
              />
            </div>
          </div>
        </div>
        <footer class="modal__footer">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="insertOpen = false"
          >Annulla</button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="findSupplier()"
            [disabled]="findLoading || !findCode"
          >
            @if (findLoading) {
              <span class="btn-spinner"></span>
            }
            🔍 Cerca
          </button>
        </footer>
      </aside>
    </div>
  }

  <!-- ─── Modal 2: New (create account for found supplier) ───────── -->
  @if (newOpen && foundSupplier) {
    <div class="modal-backdrop" (click)="closeNew()">
      <aside class="modal modal--lg" (click)="$event.stopPropagation()">
        <header class="modal__header">
          <h2 class="modal__title">➕ Nuovo fornitore</h2>
          <button type="button" class="modal-close" (click)="closeNew()" aria-label="Chiudi">×</button>
        </header>
        <div class="modal__body">
          <div class="modal-form">
            <div class="form-field">
              <label class="form-field__label">Nome</label>
              <input
                type="text"
                class="form-input"
                placeholder="Nome"
                [(ngModel)]="newForm.ac_nome"
                autocomplete="off"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Cognome</label>
              <input
                type="text"
                class="form-input"
                placeholder="Cognome"
                [(ngModel)]="newForm.ac_cognome"
                autocomplete="off"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Codice</label>
              <input
                type="text"
                class="form-input form-input--readonly strong"
                [value]="foundSupplier.codice_fornitore"
                readonly
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Ragione Sociale</label>
              <input
                type="text"
                class="form-input form-input--readonly strong"
                [value]="foundSupplier.fornitore"
                readonly
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Telefono</label>
              <input
                type="text"
                class="form-input form-input--readonly"
                [value]="foundSupplier.fornitore_telefono || '—'"
                readonly
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Email</label>
              <input
                type="text"
                class="form-input form-input--readonly"
                [value]="foundSupplier.fornitore_mail || '—'"
                readonly
              />
            </div>
            <div class="form-field form-field--full">
              <label class="form-field__label">Note</label>
              <input
                type="text"
                class="form-input"
                placeholder="Descrizione opzionale"
                [(ngModel)]="newForm.ac_note"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Password</label>
              <input
                type="password"
                class="form-input"
                placeholder="Password"
                [(ngModel)]="newForm.ac_password"
                autocomplete="new-password"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Conferma password</label>
              <input
                type="password"
                class="form-input"
                placeholder="Conferma password"
                [(ngModel)]="newForm.ac_confirm_password"
                autocomplete="new-password"
              />
            </div>
          </div>
        </div>
        <footer class="modal__footer">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="closeNew()"
          >Annulla</button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="createSupplier()"
            [disabled]="saving"
          >
            @if (saving) {
              <span class="btn-spinner"></span>
            }
            ✓ Crea fornitore
          </button>
        </footer>
      </aside>
    </div>
  }

  <!-- ─── Modal 3: Detail (read-only) ────────────────────────────── -->
  @if (detailOpen && selectedSupplier) {
    <div class="modal-backdrop" (click)="closeDetail()">
      <aside class="modal modal--lg" (click)="$event.stopPropagation()">
        <header class="modal__header">
          <h2 class="modal__title">
            🏭 {{ selectedSupplier.fornitore || 'Dettagli fornitore' }}
            <span class="status-pill status-pill--inline"
                  [attr.data-state]="isActive(selectedSupplier) ? 'active' : 'inactive'">
              @if (isActive(selectedSupplier)) { ● Attivo } @else { ● Disattivato }
            </span>
          </h2>
          <button type="button" class="modal-close" (click)="closeDetail()" aria-label="Chiudi">×</button>
        </header>
        <div class="modal__body">
          <dl class="detail-grid">
            <div class="detail-grid__row">
              <dt>Codice</dt>
              <dd class="detail-grid__val--accent">{{ selectedSupplier.codice_fornitore || '—' }}</dd>
            </div>
            <div class="detail-grid__row">
              <dt>Nome account</dt>
              <dd>
                @if (selectedSupplier.ac_nome || selectedSupplier.ac_cognome) {
                  {{ selectedSupplier.ac_nome }} {{ selectedSupplier.ac_cognome }}
                } @else { — }
              </dd>
            </div>
            <div class="detail-grid__row detail-grid__row--full">
              <dt>Ragione sociale</dt>
              <dd class="detail-grid__val--strong">{{ selectedSupplier.fornitore || '—' }}</dd>
            </div>
            <div class="detail-grid__row">
              <dt>Email</dt>
              <dd>{{ selectedSupplier.fornitore_mail || '—' }}</dd>
            </div>
            <div class="detail-grid__row">
              <dt>Telefono</dt>
              <dd>{{ selectedSupplier.fornitore_telefono || '—' }}</dd>
            </div>
          </dl>
        </div>
        <footer class="modal__footer">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="closeDetail()"
          >Chiudi</button>
          <button
            type="button"
            class="btn btn--secondary"
            (click)="openPassword()"
          >🔐 Cambia password</button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="openEdit()"
          >✏️ Modifica</button>
        </footer>
      </aside>
    </div>
  }

  <!-- ─── Modal 4: Edit ───────────────────────────────────────────── -->
  @if (editOpen && selectedSupplier) {
    <div class="modal-backdrop" (click)="closeEdit()">
      <aside class="modal modal--lg" (click)="$event.stopPropagation()">
        <header class="modal__header">
          <h2 class="modal__title">
            ✏️ Modifica fornitore
            <span class="status-pill status-pill--inline"
                  [attr.data-state]="isActive(selectedSupplier) ? 'active' : 'inactive'">
              @if (isActive(selectedSupplier)) { ● Attivo } @else { ● Disattivato }
            </span>
          </h2>
          <button type="button" class="modal-close" (click)="closeEdit()" aria-label="Chiudi">×</button>
        </header>
        <div class="modal__body">
          <div class="modal-form">
            <div class="form-field">
              <label class="form-field__label">Nome</label>
              <input
                type="text"
                class="form-input"
                placeholder="Nome"
                [(ngModel)]="editForm.ac_nome"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Cognome</label>
              <input
                type="text"
                class="form-input"
                placeholder="Cognome"
                [(ngModel)]="editForm.ac_cognome"
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Codice</label>
              <input
                type="text"
                class="form-input form-input--readonly"
                [value]="selectedSupplier.codice_fornitore || ''"
                readonly
              />
            </div>
            <div class="form-field">
              <label class="form-field__label">Email</label>
              <input
                type="email"
                class="form-input"
                placeholder="Email"
                [(ngModel)]="editForm.fornitore_mail"
              />
            </div>
            <div class="form-field form-field--full">
              <label class="form-field__label">Note</label>
              <input
                type="text"
                class="form-input"
                placeholder="Note opzionali"
                [(ngModel)]="editForm.ac_note"
              />
            </div>
          </div>
        </div>
        <footer class="modal__footer modal__footer--split">
          <div class="modal__footer-left">
            @if (isActive(selectedSupplier)) {
              <button
                type="button"
                class="btn btn--danger"
                (click)="toggleStatus()"
              >✖ Disattiva</button>
            } @else {
              <button
                type="button"
                class="btn btn--success"
                (click)="toggleStatus()"
              >✓ Riattiva</button>
            }
          </div>
          <div class="modal__footer-right">
            <button
              type="button"
              class="btn btn--ghost"
              (click)="closeEdit()"
            >Annulla</button>
            <button
              type="button"
              class="btn btn--secondary"
              (click)="openPassword()"
            >🔐 Password</button>
            <button
              type="button"
              class="btn btn--primary"
              (click)="saveEdit()"
              [disabled]="saving"
            >
              @if (saving) {
                <span class="btn-spinner"></span>
              }
              ✓ Salva
            </button>
          </div>
        </footer>
      </aside>
    </div>
  }

  <!-- ─── Modal 5: Password ──────────────────────────────────────── -->
  @if (passwordOpen && selectedSupplier) {
    <div class="modal-backdrop" (click)="passwordOpen = false">
      <aside class="modal modal--sm" (click)="$event.stopPropagation()">
        <header class="modal__header">
          <h2 class="modal__title">🔐 Cambia password</h2>
          <button type="button" class="modal-close" (click)="passwordOpen = false" aria-label="Chiudi">×</button>
        </header>
        <div class="modal__body">
          <p class="modal__hint">
            Imposta una nuova password per <strong>{{ selectedSupplier.ac_nome }} {{ selectedSupplier.ac_cognome }}</strong>.
          </p>
          <div class="modal-form modal-form--single">
            <div class="form-field form-field--full">
              <label class="form-field__label">Nuova password</label>
              <input
                type="password"
                class="form-input"
                placeholder="Nuova password"
                [(ngModel)]="newPassword"
                autocomplete="new-password"
              />
            </div>
            <div class="form-field form-field--full">
              <label class="form-field__label">Conferma password</label>
              <input
                type="password"
                class="form-input"
                placeholder="Conferma password"
                [(ngModel)]="confirmNewPassword"
                autocomplete="new-password"
              />
            </div>
          </div>
        </div>
        <footer class="modal__footer">
          <button
            type="button"
            class="btn btn--ghost"
            (click)="passwordOpen = false"
          >Annulla</button>
          <button
            type="button"
            class="btn btn--primary"
            (click)="savePassword()"
            [disabled]="saving"
          >
            @if (saving) {
              <span class="btn-spinner"></span>
            }
            ✓ Salva password
          </button>
        </footer>
      </aside>
    </div>
  }
